<template>
  <!-- Add Department -->
  <div
    class="modal fade dq-modal"
    id="add_department"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-card">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-3">
            <span class="dq-icon-badge">
              <i class="ti ti-building-community"></i>
            </span>
            <div class="lh-1">
              <h4 class="modal-title mb-1">Add Department</h4>
              <div class="dq-subtitle">
                Create a department and control whether it’s visible to HR workflows.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close-btn"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="handleAdd">
          <!-- Body -->
          <div class="modal-body dq-modal-body">
            <div class="dq-form-grid">
              <!-- Department Name -->
              <div class="dq-field">
                <label class="form-label dq-label">
                  Department Name <span class="text-danger">*</span>
                </label>

                <div class="dq-input-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-id"></i>
                  </span>
                  <input
                    v-model="addName"
                    type="text"
                    class="form-control dq-input"
                    placeholder="e.g. Human Resources"
                    required
                    autocomplete="off"
                  />
                </div>

                <div class="dq-help">
                  Use a clear name employees will recognize (e.g. HR, Finance, Operations).
                </div>
              </div>

              <!-- Status -->
              <div class="dq-field">
                <label class="form-label dq-label">
                  Status <span class="text-danger">*</span>
                </label>

                <div class="dq-select-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-toggle-left"></i>
                  </span>

                  <!-- vue-select -->
                  <vue-select
                    v-model="addStatus"
                    :options="DepartActive"
                    id="departacti"
                    placeholder="Select status"
                    :clearable="false"
                    class="dq-vselect"
                  />
                </div>

                <div class="dq-help">
                  <span class="dq-pill dq-pill-success" v-if="addStatus === 'Active'">
                    Active
                  </span>
                  <span class="dq-pill dq-pill-muted" v-else>
                    Inactive
                  </span>
                  <span class="ms-2 text-muted">
                    Inactive departments won’t appear in assignment dropdowns.
                  </span>
                </div>
              </div>

              <!-- Tip Card -->
              <div class="dq-tip">
                <div class="dq-tip-icon">
                  <i class="ti ti-bulb"></i>
                </div>
                <div>
                  <div class="dq-tip-title">Quick tip</div>
                  <div class="dq-tip-text">
                    Keep department names consistent across your org chart to avoid duplicates.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn-soft me-2"
              data-bs-dismiss="modal"
              :disabled="addLoading"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn-primary" :disabled="addLoading">
              <span v-if="addLoading" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="addLoading">Adding…</span>
              <span v-else>Add Department</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Add Department -->

  <!-- Edit Department -->
  <div
    class="modal fade dq-modal"
    id="edit_department"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-card">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="d-flex align-items-center gap-3">
            <span class="dq-icon-badge dq-icon-badge-warn">
              <i class="ti ti-edit"></i>
            </span>
            <div class="lh-1">
              <h4 class="modal-title mb-1">Edit Department</h4>
              <div class="dq-subtitle">
                Update the department details. Changes apply immediately.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn dq-close-btn"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="handleEdit">
          <!-- Body -->
          <div class="modal-body dq-modal-body">
            <div class="dq-form-grid">
              <!-- Department Name -->
              <div class="dq-field">
                <label class="form-label dq-label">
                  Department Name <span class="text-danger">*</span>
                </label>

                <div class="dq-input-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-id"></i>
                  </span>
                  <input
                    v-model="editName"
                    type="text"
                    class="form-control dq-input"
                    placeholder="e.g. Human Resources"
                    required
                    autocomplete="off"
                  />
                </div>

                <div class="dq-help">
                  Rename carefully—reports may display the updated label everywhere.
                </div>
              </div>

              <!-- Status -->
              <div class="dq-field">
                <label class="form-label dq-label">
                  Status <span class="text-danger">*</span>
                </label>

                <div class="dq-select-wrap">
                  <span class="dq-input-icon">
                    <i class="ti ti-toggle-left"></i>
                  </span>

                  <vue-select
                    v-model="editStatus"
                    :options="EditDepartActive"
                    id="editdepartacti"
                    placeholder="Select status"
                    :clearable="false"
                    class="dq-vselect"
                  />
                </div>

                <div class="dq-help">
                  <span class="dq-pill dq-pill-success" v-if="editStatus === 'Active'">
                    Active
                  </span>
                  <span class="dq-pill dq-pill-muted" v-else>
                    Inactive
                  </span>
                  <span class="ms-2 text-muted">
                    Inactive departments won’t be selectable for new assignments.
                  </span>
                </div>
              </div>

              <!-- Info strip -->
              <div class="dq-info-strip">
                <i class="ti ti-shield-check me-2"></i>
                Changes are audited in your system logs (if enabled).
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn-soft me-2"
              data-bs-dismiss="modal"
              :disabled="editLoading"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn-primary" :disabled="editLoading">
              <span v-if="editLoading" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="editLoading">Saving…</span>
              <span v-else>Save Department</span>
              <i class="ti ti-device-floppy ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Edit Department -->

  <!-- Delete Modal -->
  <div
    class="modal fade dq-modal"
    id="delete_modal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-card">
        <div class="modal-body dq-delete-body">
          <div class="dq-danger-hero">
            <span class="dq-danger-badge">
              <i class="ti ti-trash-x"></i>
            </span>
          </div>

          <h4 class="mb-1 dq-delete-title">Confirm delete</h4>
          <p class="mb-3 dq-delete-subtitle">
            This action permanently removes the department. You can’t undo this.
          </p>

          <div class="dq-delete-warning">
            <i class="ti ti-alert-triangle me-2"></i>
            Tip: If this department is used in historical records, consider setting it to
            <strong>Inactive</strong> instead.
          </div>

          <div class="d-flex justify-content-center gap-2 mt-4">
            <button
              type="button"
              class="btn btn-light dq-btn-soft"
              data-bs-dismiss="modal"
              :disabled="deleteLoading"
            >
              Cancel
            </button>

            <button class="btn btn-danger dq-btn-danger" @click="handleDelete" :disabled="deleteLoading">
              <span v-if="deleteLoading" class="spinner-border spinner-border-sm me-2"></span>
              <span v-if="deleteLoading">Deleting…</span>
              <span v-else>Yes, Delete</span>
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /Delete Modal -->
</template>

<script setup>
import { ref, defineExpose } from "vue";
import { useDepartmentStore } from "@/stores/departmentStore";

const emit = defineEmits(["refresh"]);

const DepartActive = ["Active", "Inactive"];
const EditDepartActive = ["Active", "Inactive"];
const departmentStore = useDepartmentStore();

// --- Add logic
const addName = ref("");
const addStatus = ref("Active");
const addLoading = ref(false);

async function handleAdd() {
  if (!addName.value || !addStatus.value) return;
  addLoading.value = true;
  try {
    await departmentStore.createDepartment({
      name: String(addName.value).trim(),
      status: addStatus.value,
    });
    closeModal("add_department");
    addName.value = "";
    addStatus.value = "Active";
    emit("refresh");
  } finally {
    addLoading.value = false;
  }
}

// --- Edit logic
const editId = ref(null);
const editName = ref("");
const editStatus = ref("");
const editLoading = ref(false);

function openEditModal(record) {
  editId.value = record.id;
  editName.value = record.Department;
  editStatus.value = record.Status;
}

async function handleEdit() {
  if (!editName.value || !editStatus.value) return;
  editLoading.value = true;
  try {
    await departmentStore.updateDepartment({
      id: editId.value,
      name: String(editName.value).trim(),
      status: editStatus.value,
    });
    closeModal("edit_department");
    emit("refresh");
  } finally {
    editLoading.value = false;
  }
}

// --- Delete logic
const deleteId = ref(null);
const deleteLoading = ref(false);

function openDeleteModal(record) {
  deleteId.value = record.id;
}

async function handleDelete() {
  if (!deleteId.value) return;
  deleteLoading.value = true;
  try {
    await departmentStore.deleteDepartments([deleteId.value]);
    closeModal("delete_modal");
    deleteId.value = null;
    emit("refresh");
  } finally {
    deleteLoading.value = false;
  }
}

function closeModal(id) {
  const modalEl = document.getElementById(id);
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

defineExpose({
  openEditModal,
  openDeleteModal,
});
</script>

<style scoped>
/* ===== World-class modal polish (UI only) ===== */
.dq-modal .modal-dialog {
  /* Slightly larger feel without changing your bootstrap sizing */
  transform: translateY(0);
}

.dq-modal-card {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 18px 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(255, 255, 255, 1));
}

.dq-subtitle {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 2px;
}

.dq-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.dq-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
  font-size: 20px;
}
.dq-icon-badge-warn {
  background: rgba(245, 158, 11, 0.14);
  color: rgba(217, 119, 6, 1);
}

.dq-modal-body {
  padding: 18px;
  max-height: 70vh;
  overflow: auto;
}

.dq-form-grid {
  display: grid;
  gap: 14px;
}

.dq-label {
  font-weight: 700;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}

.dq-input-wrap,
.dq-select-wrap {
  position: relative;
}

.dq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}

.dq-input {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}

/* vue-select styling (works whether scoped or global) */
.dq-vselect :deep(.vs__dropdown-toggle) {
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding-left: 40px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-vselect :deep(.vs__dropdown-toggle:focus-within) {
  border-color: rgba(59, 130, 246, 0.45);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12);
}
.dq-vselect :deep(.vs__search),
.dq-vselect :deep(.vs__selected) {
  margin: 0;
}
.dq-vselect :deep(.vs__dropdown-menu) {
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 18px 40px rgba(16, 24, 40, 0.16);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  margin-top: 6px;
}

.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.75rem;
}
.dq-pill-success {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}
.dq-pill-muted {
  background: rgba(148, 163, 184, 0.18);
  color: rgba(71, 85, 105, 1);
}

/* Tip card */
.dq-tip {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.12);
}
.dq-tip-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.14);
  color: rgba(79, 70, 229, 1);
}
.dq-tip-title {
  font-weight: 800;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 2px;
}
.dq-tip-text {
  font-size: 0.83rem;
  color: rgba(17, 24, 39, 0.6);
}

/* Info strip */
.dq-info-strip {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(2, 132, 199, 0.08);
  border: 1px solid rgba(2, 132, 199, 0.12);
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.82rem;
  font-weight: 600;
}

/* Footer */
.dq-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px 18px;
}

.dq-btn-soft {
  border-radius: 12px;
  padding: 10px 14px;
}

.dq-btn-primary {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
}

/* Delete modal */
.dq-delete-body {
  padding: 18px;
  text-align: center;
}
.dq-danger-hero {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}
.dq-danger-badge {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.12);
  color: rgba(220, 38, 38, 1);
  font-size: 26px;
}
.dq-delete-title {
  font-weight: 900;
  letter-spacing: -0.02em;
}
.dq-delete-subtitle {
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.92rem;
  line-height: 1.35rem;
}
.dq-delete-warning {
  text-align: left;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(245, 158, 11, 0.10);
  border: 1px solid rgba(245, 158, 11, 0.14);
  color: rgba(120, 53, 15, 0.95);
  font-size: 0.84rem;
}
.dq-btn-danger {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(220, 38, 38, 0.14);
}
</style>
